import {useQuery} from "@apollo/client";
import classnames from "classnames";
import pluralize from "pluralize";
import {useEffect} from "react";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import client from "@/lib/api/apolloClient.ts";
import {AccountStatsResults, GQL_ACCOUNT_STATS} from "@/lib/queries/billing.ts";
import {AccountSubscriptionType} from "@/lib/types/accounts.ts";
import {AccountStats} from "@/lib/types/billing.ts";
import {CheckFrequency} from "@/lib/types/devices.ts";
import {AccountUsageActionBar} from "@/pages/accounts/account-usage/AccountUsageActionBar.tsx";
import {
  AccountUsageFeatureRow,
  AccountUsageGenericRow,
  AccountUsagePlainValueRow,
  AccountUsageRowPair,
} from "@/pages/accounts/account-usage/AccountUsageTableRows.tsx";

import css from "./AccountUsageTable.module.css";

export const AccountUsageTable = () => {
  const {user} = useCurrentUserContext();
  const isFreeTrial = (user?.account.free_trial_expires_at as string | null | undefined) !== null;
  const {data} = useQuery<AccountStatsResults>(GQL_ACCOUNT_STATS);
  const accountsData: AccountStats[] = data?.accountStats.items || [];

  const colspan = accountsData.length + 1;
  useEffect(() => {
    client.refetchQueries({include: [GQL_ACCOUNT_STATS]});
  }, []);
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="text-gray-700">Review limits and usage per account.</div>
        <AccountUsageActionBar />
      </div>
      <div className="table-wrapper" style={{overflowX: "auto", WebkitOverflowScrolling: "touch"}}>
        <table className="table shadow-sm rounded-top rounded-bottom">
          <thead>
            <tr>
              <th className="text-uppercase p-2">Feature</th>
              {accountsData.map((ad, idx) => (
                <th className="text-uppercase p-2" key={`heading-${idx}`}>
                  {ad.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td colSpan={colspan} className={classnames("py-3", {[css.roundedCell]: accountsData.length == 1})}>
                <h3 className={classnames(css.sectionHeader)}>Monitoring</h3>
              </td>
            </tr>
            <AccountUsageRowPair name="Checks used" field="services" data={accountsData} />
            <AccountUsageRowPair name="Basic Checks Used" field="basic_checks" data={accountsData} className="ps-4" />
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_2_0 && (
              <AccountUsageRowPair
                name="Advanced Checks Used"
                field="advanced_checks"
                data={accountsData}
                className="ps-4"
              />
            )}
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0 && (
              <AccountUsageRowPair
                name="Transaction Checks Used"
                field="transaction_checks"
                data={accountsData}
                className="ps-4"
              />
            )}
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0 && (
              <AccountUsageRowPair name="RUM Checks Used" field="rum_checks" data={accountsData} className="ps-4" />
            )}
            <AccountUsageRowPair
              name="Monthly RUM Requests Used"
              field="rum_requests"
              data={accountsData}
              helpText="RUM Requests reset on the first of each month."
              className="ps-4"
            />

            {user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0 && (
              <AccountUsageRowPair
                name="Page Speed Checks Used"
                field="pagespeed_checks"
                data={accountsData}
                className="ps-4"
              />
            )}
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0 && (
              <AccountUsageRowPair name="API Checks Used" field="api_checks" data={accountsData} className="ps-4" />
            )}
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0 && (
              <AccountUsageRowPair
                name="Custom Checks Used"
                field="custom_checks"
                data={accountsData}
                helpText="Includes Heartbeat and Incoming Webhook checks."
                className="ps-4"
              />
            )}
            <AccountUsageRowPair name="Group Checks Used" field="group_checks" data={accountsData} className="ps-4" />
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0 && (
              <AccountUsageRowPair
                name="CloudStatus Checks Used"
                field="cloudstatus_checks"
                data={accountsData}
                className="ps-4"
              />
            )}
            <AccountUsageGenericRow
              name="Minimum Check Frequency"
              data={accountsData}
              getterFn={(data) =>
                data.account
                  ? {
                      [CheckFrequency.HIGH]: "1 min",
                      [CheckFrequency.MEDIUM]: "3 min",
                      [CheckFrequency.LOW]: "5 min",
                    }[data.account.limit_check_frequency]
                  : ""
              }
            />
            <AccountUsageFeatureRow
              name="Content Matching Available"
              field="enable_string_matching"
              data={accountsData}
            />
            <AccountUsageRowPair
              name="Locations Available"
              field="locations"
              data={accountsData}
              helpText={
                (accountsData?.[0]?.locations?.length ?? 0) > 1
                  ? `Please Note: Your available monitoring locations are based on your subscription. The highest tier includes access to all ${accountsData?.[0]?.locations?.[1]} locations.`
                  : undefined
              }
            />
            <AccountUsageRowPair
              name="Private Location Servers"
              field="private_locations"
              data={accountsData}
              helpText="Private check servers used to monitor internal-only infrastructure."
            />
            <tr>
              <td className="py-3" colSpan={colspan}>
                <h3 className={css.sectionHeader}>Notifications</h3>
              </td>
            </tr>
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0 && (
              <AccountUsageRowPair
                name="Monthly SMS/WhatsApp Used"
                field="sms"
                data={accountsData}
                helpText="WhatsApp messages draw from the SMS allowance and are counted here. SMS and phone call credits for paid subscribers reset on the first of each month."
              />
            )}
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_1_0 && (
              <AccountUsageRowPair
                name="Monthly Phone Calls Used"
                field="phonecall"
                data={accountsData}
                helpText="SMS and phone call credits for paid subscribers reset on the first of each month."
              />
            )}
            {user?.account.subscription_type === AccountSubscriptionType.BILLING_2_0 && (
              <AccountUsageRowPair
                name="Monthly SMS, Phone And WhatsApp Alerts Used"
                field="sms_and_phone_alerts"
                data={accountsData}
                helpText="WhatsApp messages draw from this allowance and are counted here. SMS and phone call credits for paid subscribers reset on the first of each month."
              />
            )}
            <AccountUsageGenericRow
              name="Limit SMS/Call Countries"
              data={accountsData}
              getterFn={(data) =>
                data?.account ? (data.account.enable_international_sms ? "All Countries" : "US and GB only") : ""
              }
            />
            <AccountUsagePlainValueRow name="Extra SMS Credits" field="sms_credits" data={accountsData} />
            <tr>
              <td className="py-3" colSpan={colspan}>
                <h3 className={css.sectionHeader}>Monitoring Reports and Dashboards</h3>
              </td>
            </tr>
            <AccountUsageRowPair name="Reports Used" field="reports" data={accountsData} />
            <AccountUsageRowPair name="Dashboards Used" field="dashboards" data={accountsData} />
            <tr>
              <td className="py-3" colSpan={colspan}>
                <h3 className={css.sectionHeader}>Public and Private Status Pages</h3>
              </td>
            </tr>
            <AccountUsageRowPair name="Status Pages Used" field="statuspages" data={accountsData} />
            <AccountUsageRowPair
              name="Status Page Subscriptions Used"
              field="statuspages_subscriptions"
              data={accountsData}
              helpText={
                isFreeTrial
                  ? "Free trial users have limited # of subscribers. On paid plans subscriber limit is significantly higher."
                  : "Number of Email & SMS Subscribers available on StatusPage."
              }
            />
            <AccountUsageRowPair
              name="Status Pages External Users Used"
              field="statuspages_external_user"
              data={accountsData}
            />
            <AccountUsageRowPair name="Status Page SMS Used" field="sp_sms" data={accountsData} />
            <tr>
              <td className="py-3" colSpan={colspan}>
                <h3 className={css.sectionHeader}>Account and Settings</h3>
              </td>
            </tr>
            <AccountUsageGenericRow
              name="History Retention"
              data={accountsData}
              getterFn={(data) =>
                data?.account ? `${data?.account.limit_history} ${pluralize("month", data?.account.limit_history)}` : ""
              }
            />
            <AccountUsageRowPair name="Multiple Users Used" field="multi_users" data={accountsData} />
            <AccountUsageFeatureRow name="API Access Available" field="enable_api_access" data={accountsData} />
            <AccountUsageFeatureRow name="SSO Available" field="enable_sso" data={accountsData} />
            <AccountUsageFeatureRow name="Audit Log Available" field="enable_audit_log" data={accountsData} />
            <AccountUsageFeatureRow
              name="Advanced Features Available"
              field="enable_advanced_features"
              data={accountsData}
              helpText="Includes custom-branded reporting."
            />
            <AccountUsageFeatureRow
              name="Enterprise Features Available"
              field="enable_enterprise_features"
              data={accountsData}
              helpText="Includes traceroute and bulk check export."
            />
          </tbody>
        </table>
      </div>
    </div>
  );
};
